import { useCallback } from 'react';

import { useSetAppFiltersByPromptAppIds } from '@atlassian/conversation-assistant-chat-prompt-input/use-set-app-filters-by-app-ids';
import { useUnconnectedAppsForPrompt } from '@atlassian/conversation-assistant-chat-prompt-input/use-unconnected-apps-for-prompt';
import { useAppConnectionBannerActions } from '@atlassian/conversation-assistant-chat-prompt-input/useAppConnectionBannerActions';
import { useChatModeStoreActions } from '@atlassian/conversation-assistant-chat-prompt-input/useChatModeStoreActions';
import type { AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';

export type UsePromptAppConnectorsOptions = {
	conversationId: string;
	assistanceServiceParams: AssistanceServiceInputParam['assistanceServiceParams'];
};

export type HandlePromptAppConnectorsOptions = {
	/** When true, connect buttons are displayed on the next line (e.g. in sidebar) */
	forceMultiline?: boolean;
	/**
	 * When true, a connected app keeps its row and shows a "Connected" state
	 * instead of being removed (3P suggested-prompts experiment).
	 */
	showConnectedState?: boolean;
};

/**
 * The 3P connect prompt only ever offers this many connectors, keeping it concise —
 * the most-active unconnected ones from the API's activity-ranked
 * `third_party_connectors`.
 */
const MAX_CONNECT_PROMPT_APPS = 2;

/**
 * Hook that handles app connector setup when selecting a prompt:
 * - Sets app filters based on prompt's appIds
 * - Shows app connection banner for unconnected apps
 * - Resets filters when no appIds are specified
 */
export function usePromptAppConnectors({
	conversationId,
	assistanceServiceParams,
}: UsePromptAppConnectorsOptions): {
	handlePromptAppConnectors: (
		appIds: string[] | undefined,
		options?: HandlePromptAppConnectorsOptions,
	) => void;
} {
	const { setAppFiltersByPromptAppIds } = useSetAppFiltersByPromptAppIds({
		assistanceServiceParams,
	});
	const { getUnconnectedApps } = useUnconnectedAppsForPrompt(assistanceServiceParams);
	const { showBanner: showAppConnectionBanner, hideBanner: hideAppConnectionBanner } =
		useAppConnectionBannerActions();
	const { setOrgKnowledge } = useChatModeStoreActions();

	/**
	 * Handles app connectors and connection banner for a selected prompt card.
	 * Call this when a user selects a prompt that may have associated apps.
	 */
	const handlePromptAppConnectors = useCallback(
		(appIds: string[] | undefined, options?: HandlePromptAppConnectorsOptions): void => {
			if (appIds?.length) {
				setAppFiltersByPromptAppIds(conversationId, appIds);
				const unconnectedApps = getUnconnectedApps(appIds);
				let appsForPrompt = unconnectedApps;
				if (options?.showConnectedState) {
					// 3P connect prompt: offer the most-active *unconnected* connectors. Rank
					// first and cap second — capping `appIds` first would offer nothing at all
					// whenever the top-ranked connectors happen to be connected already.
					// `getUnconnectedApps` returns store order, so re-key by source to recover
					// the API's ranking. The app filters above still use the full set.
					const bySource = new Map(unconnectedApps.map((app) => [app.source, app]));
					appsForPrompt = appIds
						.flatMap((appId) => {
							const app = bySource.get(appId);
							return app ? [app] : [];
						})
						.slice(0, MAX_CONNECT_PROMPT_APPS);
				}
				if (appsForPrompt.length > 0) {
					showAppConnectionBanner(appsForPrompt, {
						forceMultiline: options?.forceMultiline,
						showConnectedState: options?.showConnectedState,
					});
				} else {
					hideAppConnectionBanner();
				}
			} else {
				setOrgKnowledge(conversationId, { filteredApps: [] });
				hideAppConnectionBanner();
			}
		},
		[
			conversationId,
			getUnconnectedApps,
			setAppFiltersByPromptAppIds,
			setOrgKnowledge,
			showAppConnectionBanner,
			hideAppConnectionBanner,
		],
	);

	return { handlePromptAppConnectors };
}
